import {CircularProgress, MenuItem, Select, Typography} from '@mui/material';
import {DataGrid, GridCellParams, GridColDef} from '@mui/x-data-grid';
import React, {FunctionComponent, useEffect, useState} from 'react';
import {Edit2, Plus} from 'react-feather';
import {useTranslation} from 'react-i18next';
import {useNavigate, useSearchParams} from 'react-router';
import Swal from 'sweetalert2';
import {errorBigIcon, iconNotifySuccess} from '../../../assets/icons/icons';
import CardFormDialog from '../../../components/Dialogs/CardFormDialog/CardFormDialog';
import {Button, Chip, WhiteCard} from '../../../components/StyledComponents';
import {
  EPlatformPrices,
  EPlatformsNames,
  EProPlatformPrices,
  errorSubscriptionStatuses,
  EStripeSubStatus,
  ETrialStatus,
  inactiveSubscriptionStatuses,
  unpaidSubscriptionStatuses,
} from '../../../models/consts';
import {IError} from '../../../models/inner-models';
import {IHistoryItem, IPlatformSubscription} from '../../../models/models';
import Platform from '../../../models/platforms-data';
import {
  cancelOneSubscription,
  getAllSubscriptions,
  getStripeDefaultPaymentMethod,
  getStripePrices,
  getUsersPaymentsHistory,
} from '../../../store/actions';
import {useAppDispatch, useAppSelector} from '../../../store/hooks';
import {
  chatgptActualPlan,
  claudeActualPlan,
  githubActualPlan,
  gitlabActualPlan,
  jiraActualPlan,
  loadingUser,
  notionActualPlan,
  paymentHistorySelector,
  shopifyActualPlan,
  shopifySubscription,
  shopifyTrialData,
  shopifyTrialHoursLeft,
  trelloActualPlan
} from '../../../store/selectors';
import {decoratePopUpMessage} from '../../../utils/popUpTextDecorator';
import withRedirectAfterLogout from '../../../utils/withRedirectAfterLogout';
import './style.scss';
import {iniFrame} from "../../../serviceWorker";
import {BadgesIFrameMode} from "../../../components/Badges/Badges";
import Offboarding from "./Offboarding/Offboarding";
import {More} from 'iconsax-reactjs';

export interface IPlatformSubscriptionRow extends IPlatformSubscription {
  id: number;
}

interface ICancelData {
  platform: string;
  period: string;
  isTrial: boolean;
}


// eslint-disable-next-line @typescript-eslint/no-explicit-any
const platformPlansLocal: Record<string, any> = {
  github: {cost: 12, amount: 10},
  gitlab: {cost: 12, amount: 10},
  trello: {cost: 10, amount: 10},
  jira: {cost: 2.50, amount: 1},
  notion: {cost: 10, amount: 1},
  shopify: {cost: 0, amount: 100},
  chatgpt: {cost: 10, amount: 1},
  claude: {cost: 10, amount: 1},
}

const Subscription: FunctionComponent = () => {
  const dispatch = useAppDispatch();
  const {t: translate} = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const githubSubItem = useAppSelector(githubActualPlan);
  const trelloSubItem = useAppSelector(trelloActualPlan);
  const gitlabSubItem = useAppSelector(gitlabActualPlan);
  const jiraSubItem = useAppSelector(jiraActualPlan);
  const notionSubItem = useAppSelector(notionActualPlan);
  const shopifySubItem = useAppSelector(shopifyActualPlan);
  const chatgptSubItem = useAppSelector(chatgptActualPlan);
  const claudeSubItem = useAppSelector(claudeActualPlan);

  const userLoading = useAppSelector(loadingUser);
  const paymentHistoryItems = useAppSelector(paymentHistorySelector);
  const shopifyTrialHours = useAppSelector(shopifyTrialHoursLeft);
  const shopifyTrial = useAppSelector(shopifyTrialData);
  const shopifySub = useAppSelector(shopifySubscription);

  const [rows, setRows] = useState<Array<IPlatformSubscriptionRow>>();
  const [rowsActive, setRowsActive] = useState<Array<IPlatformSubscriptionRow>>();
  const [rowsAvailable, setRowsAvailable] = useState<Array<IPlatformSubscriptionRow>>();
  const [cardModalOpen, setCardModalOpen] = useState<boolean>(false);
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [cancelData, setCancelData] = useState<ICancelData | null>(null);

  const closeCancelModal = () => setCancelData(null);

  useEffect(() => {
    const errShopifyMessage = searchParams.get('reason');

    if (errShopifyMessage) {
      navigate('/subscriptions')
      Swal.fire({
        imageUrl: errorBigIcon,
        title: translate('notifications.titles.error'),
        text: decoratePopUpMessage(translate('notifications.shopify_user.errorBuy', {err: errShopifyMessage})),
      });
    }

    const fetchData = async () => {
      setLoadingData(true);
      try {
        await dispatch(getStripePrices()).unwrap().then(res => {
          // setStripeSub({...res.result})
        })
        dispatch(getUsersPaymentsHistory()).unwrap();
        await Promise.all([dispatch(getAllSubscriptions()).unwrap(), dispatch(getStripeDefaultPaymentMethod()).unwrap()])
      } catch (e) {
        console.log(e);
      } finally {
        setLoadingData(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (userLoading) {
      return;
    }
    setRows([githubSubItem, gitlabSubItem, trelloSubItem, jiraSubItem, notionSubItem, shopifySubItem, chatgptSubItem, claudeSubItem]);
  }, [githubSubItem, trelloSubItem, gitlabSubItem, jiraSubItem, notionSubItem, shopifySubItem, chatgptSubItem, claudeSubItem, userLoading]);

  useEffect(() => {
    if (rows) {
      const started = rows.filter(item => item.status !== 'not started' && item.isConnect);
      const others = rows.filter(item => (item.status === 'not started' || !item.isConnect));
      setRowsActive(started);
      setRowsAvailable(others);
    }
  }, [rows]);

  const openPaymentHistory = (platform: string, paymentList: IHistoryItem[] | undefined) => {
    if (!paymentList) {
      return;
    }
    navigate(`/payment-history?platform=${platform}`)
  };

  const cancelOneSub = (platform: string) => {
    const onConfirmStopSub = async () => {
      try {
        dispatch(cancelOneSubscription(platform)).unwrap();

        await Swal.fire({
          title: translate('notifications.titles.success'),
          text: translate('notifications.subscription.canceled_successfully'),
          imageUrl: iconNotifySuccess,
        });
        location.reload();

      } catch (err) {
        const error = err as IError;

        await Swal.fire({
          title: translate('notifications.titles.error'),
          text: decoratePopUpMessage(error.error as string),
          imageUrl: errorBigIcon,
        });
      }
    };

    return Swal.fire({
      title: translate('notifications.titles.warning'),
      text: translate('notifications.subscription.cancel_confirm'),
      imageUrl: errorBigIcon,
      confirmButtonText: translate('notifications.choices.continue'),
      cancelButtonText: translate('notifications.choices.cancel'),
      showCancelButton: true,
      showLoaderOnConfirm: true,
      confirmButtonColor: '#3B82F6',
      preConfirm: () => {
        return onConfirmStopSub();
      },
      allowOutsideClick: () => !Swal.isLoading(),
    });
  };

  const columns: GridColDef<IPlatformSubscriptionRow>[] = [
    {
      field: 'platformName',
      headerName: translate('subscriptions.subscriptions_list.app'),
      width: 150,
      editable: false,
      sortable: false,
      headerClassName: 'subscription-table--header',
      renderCell: (params: GridCellParams) => (
        <div className='subscriptions-logo'>
          <img src={Platform[params.value as EPlatformsNames]?.smallLogo} className="platformLogo" loading="lazy"/>
          {Platform[params.value as EPlatformsNames]?.title}
        </div>
      ),
    },
    {
      field: 'plan',
      headerName: translate('subscriptions.subscriptions_list.planName'),
      width: 100,
      editable: false,
      sortable: false,
      headerClassName: 'subscription-table--header',
      cellClassName: 'plan',
      renderCell: (params: GridCellParams) => {
        return !params.value || params.value === '-' ? 'N/A' : params.value === 'Trial' ? 'Free Trial' : params.value
      }
    },
    {
      field: 'planQuantity',
      headerName: translate('subscriptions.subscriptions_list.quantity'),
      flex: 1,
      editable: false,
      sortable: false,
      headerClassName: 'subscription-table--header',
      cellClassName: 'plan-quantity',
      renderCell: (params: GridCellParams) => {
        const platformName = params.row.platformName
        return platformName === 'chatgpt' || platformName === 'claude' ? 'Unlimited conversations' : params.value === 0 ?
          params.row.status === 'active' && platformName === 'shopify' ? '0 order' :
            `${platformPlansLocal[platformName]?.amount}+ ${platformName === 'jira' ? ' user' : platformName === 'shopify' ? ' orders' : translate(`views.bulk_restore.itemName.${platformName}.many`)} ${platformName === 'github' ? '& Gists' : platformName === 'gitlab' ? '& Snippets' : ''}` :
          params.value === 1 ?
            (platformName === 'jira' ? '1 user' : platformName === 'shopify' ? '1 Order' : '1 Workspace') :
            params.value === 2002 ?
              'Unlimited users' :
              `${params.row.status === 'not started' ?
                `${params.value}+` :
                params.value} ${platformName === 'jira' ? 'users' : platformName === 'shopify' ? 'orders' : translate(`views.bulk_restore.itemName.${platformName}.many`)} ${platformName === 'github' ? '& Gists' : platformName === 'gitlab' ? '& Snippets' : ''}`
      }
    },
    {
      field: 'price',
      headerName: translate('subscriptions.subscriptions_list.price'),
      flex: 1,
      editable: false,
      sortable: false,
      headerClassName: 'subscription-table--header',
      cellClassName: 'price-cell',
      renderCell: (params: GridCellParams) => {
        const planStart = params.row.platformName === 'shopify' ? Platform[params.row.platformName].plans[0].defaultPlan.cost : (Platform[params.row.platformName].plans[0].defaultPlan.cost || 5)
        const isShopifyTrialActive = params.row.platformName === 'shopify' &&
          shopifyTrial?.trialStatus === ETrialStatus.STARTED &&
          shopifySub?.subscription_type;

        return (
          <div className="price">
            {params.row.plan === 'Trial' ?
              params.row.status === 'not started' ?
                `Plans start from ${(planStart).toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD"
                })} ${params.row.platformName === 'jira' ? 'per user' : ''}` :
                'Free'
              : ((params.value.split(' / ')[0] === '$0.00' || params.value === '-') && params.row.platformName !== 'shopify') ?
                `Plans start from ${(planStart).toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD"
                })} ${params.row.platformName === 'jira' ? 'per user' : ''}` :
                <>
                  {params.value.split(' / ')[0]}
                  {isShopifyTrialActive && shopifyTrialHours > 0 && (
                    <span style={{marginLeft: '8px', fontSize: '14px', color: '#666'}}>
                      ({shopifyTrialHours}h trial left)
                    </span>
                  )}
                </>
            }
            {(params.value !== '-' && (!!params.row.planQuantity || params.row.platformName === 'shopify')) && (
              <div className='price-sub'>
                {params.row.isCancel ?
                  'Canceled' :
                  params.value.split(' / ')[1] === 'yr.' ? 'Billed annually' : 'Billed monthly'
                }
              </div>
            )}
          </div>
        )
      },
    },
    {
      field: 'status',
      headerName: translate('subscriptions.subscriptions_list.status'),
      flex: 1,
      editable: false,
      sortable: false,
      headerClassName: 'subscription-table--header',
      cellClassName: 'subscription-table--cell',
      renderCell: (params: GridCellParams) => {
        let subscriptionStatusClass = 'active';
        let subscriptionStatus = translate('subscriptions.statuses.active');

        if (params.row.isConnect && (unpaidSubscriptionStatuses.includes(params.value as EStripeSubStatus) || params.row.plan === 'Trial' && params.row.status === 'active')) {
          subscriptionStatusClass = 'unpaid';
          subscriptionStatus = params.row.plan === 'Trial' ? 'Trial period' : params.value.replaceAll('_', ' ');
        } else if (!params.row.isConnect || params.row.plan === 'Trial' && params.row.status === 'not started' || errorSubscriptionStatuses.includes(params.value as EStripeSubStatus)) {
          subscriptionStatusClass = 'error';
          subscriptionStatus = params.value.replaceAll('_', ' ');
          if (!params.row.isConnect || params.row.status === 'not started' && params.row.plan === 'Trial') {
            subscriptionStatus = 'Not connected'
          }
        } else if (inactiveSubscriptionStatuses.includes(params.value as EStripeSubStatus)) {
          subscriptionStatusClass = 'inactive';
          subscriptionStatus = params.value.replaceAll('_', ' ');
        }

        return (
          <div className='status'>
            <Chip label={subscriptionStatus} className={subscriptionStatusClass}/>
            {/*<br/>*/}
            {params.row.finishDate && params.row.status === 'active' && params.row.isConnect && (//&& params.row.plan !== 'Trial'
              <span className='active-until'><span>Until:</span> {params.row.finishDate}</span>
            )}
          </div>
        );
      },
    },
    {
      field: '',
      width: 200,
      editable: false,
      sortable: false,
      headerClassName: 'subscription-table--header',
      cellClassName: 'action-cell',
      renderCell: (params: GridCellParams) => {
        const paymentList = paymentHistoryItems?.filter((item) => item.product_id === EPlatformPrices[params.row.platformName.toLowerCase()] || item.product_id === EProPlatformPrices[params.row.platformName.toLowerCase()])

        return (
          <div className="last-column">
            <Button
              color="primary"
              variant={params.row.plan !== 'Trial' ? params.row.status === 'active' && params.row.isConnect
                ? "outlined" : "contained" : "contained"}
              onClick={() => {
                if ((params.row.plan === 'Trial' && params.row.status === 'not started') || !params.row.isConnect) {
                  navigate('/app-connector');
                } else {
                  if (localStorage['checkoutContact']) {
                    localStorage.removeItem('checkoutContact')
                  }
                  localStorage.setItem('checkout-platform', params.row.platformName)
                  params.row.platformName === 'shopify' ? navigate('/platform/dashboard/shopify-prices-page') : navigate('/checkout');
                }
              }}
              disabled={
                params.row.plan !== 'Trial' && params.row.isConnect && params.row.status === 'active' &&
                (((params.row.platformName === 'jira' || params.row.platformName === 'shopify') && params.row.planQuantity > 2000) || ((params.row.platformName === 'trello' || params.row.platformName === 'notion') && params.row.planQuantity > 200) ||
                  ((params.row.platformName === 'github' || params.row.platformName === 'gitlab') && params.row.planQuantity > 1000))
              }
            >
              {params.row.plan !== 'Trial' && params.row.status === 'active' && params.row.isConnect && (
                <Edit2 height={16} width={16} className='action-cell-icon'/>
              )}
              {params.row.plan !== 'Trial' && params.row.isConnect ? params.row.status === 'active'
                  ? translate('subscriptions.edit_sub')
                  : translate('subscriptions.buy_subscription') :
                !params.row.isConnect && params.row.status !== 'not started' ?
                  translate('subscriptions.connect_now') :
                  params.row.status === 'not started' ?
                    translate('subscriptions.start_trial') :
                    translate('subscriptions.buy_subscription')
              }
            </Button>

            <Select
              value='more'
              className='subscriptions-select'
              MenuProps={{id: 'sub-more-select'}}
            >
              <MenuItem value='more' disabled >
                <More className='subscriptions-select-more'/>
              </MenuItem>
              <MenuItem
                value='history-list'
                onClick={() => openPaymentHistory(params.row.platformName, paymentList)}
                disabled={!paymentHistoryItems || !paymentList || !paymentList?.length}
                style={{display: 'block'}}
              >
                {translate('common.default_pages.payment_history')}
              </MenuItem>
              <MenuItem
                value='cancel-sub'
                sx={{color: '#EF4444'}}
                onClick={() => setCancelData({
                  platform: params.row.platformName,
                  period: params.row.finishDate,
                  isTrial: params.row.plan === 'Trial'
                })} // cancelOneSub(params.row.platformName)}
                disabled={params.row.status !== 'active' || params.row.isCancel}
                style={{display: 'block'}}
              >
                {params.row.plan === 'Trial' ? translate('subscriptions.cancel_trial') : translate('subscriptions.cancel_subscription')}
              </MenuItem>
            </Select>
          </div>
        )
      },
    }
  ];

  return (
    <div id="subscription-page">
      {iniFrame() &&
        <BadgesIFrameMode/>
      }
      <WhiteCard>
        {userLoading || loadingData || !rows?.length ?
          <div className="spinner-wrapper">
            <CircularProgress/>
          </div> :
          <div className="subscriptions-block">
            {!!rowsActive?.length && (
              <>
                <Typography variant="h2" display="block" mb='32px' className='with-control'>
                  {translate('subscriptions.subscriptions_list.subs_active')}
                  <Button mr={2} variant="contained" color="primary"
                          onClick={() => navigate('/app-connector')}
                  >
                    <Plus/>
                    {translate('common.buttons.addPlatform')}
                  </Button>
                </Typography>
                <DataGrid
                  disableColumnMenu
                  disableColumnFilter
                  rows={rowsActive}
                  disableRowSelectionOnClick
                  rowHeight={60}
                  hideFooter
                  autoHeight
                  columns={columns}
                  disableColumnSelector
                />
              </>
            )}
            {!!rowsAvailable?.length && (
              <>
                <Typography
                  variant="h2" display="block" mb='32px' className='with-control'
                  mt={!!rowsActive?.length ? '48px' : '0px'}
                >
                  {translate('subscriptions.subscriptions_list.subs_available')}
                </Typography>
                <DataGrid
                  disableColumnMenu
                  disableColumnFilter
                  rows={rowsAvailable}
                  disableRowSelectionOnClick
                  rowHeight={87}
                  hideFooter
                  autoHeight
                  columns={columns}
                  disableColumnSelector
                />
              </>
            )}
          </div>
        }
      </WhiteCard>
      <CardFormDialog
        isOpen={cardModalOpen}
        closeNotification={() => setCardModalOpen(false)}
      />
      {!!cancelData && (
        <Offboarding platform={cancelData?.platform} period={cancelData?.period} isTrial={cancelData?.isTrial}
                     onClose={closeCancelModal}/>
      )}
    </div>
  );
};

export default withRedirectAfterLogout()(Subscription);
